import { useCallback, useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";

import { APP_ROUTES } from "@/core/constants";
import { AppStore } from "@/core/data_sources/store/app";
import type { AppOverlayPanelStateType } from "@/core/data_sources/store/app/app_state";
import ErrorHandler from "@/core/utils/error_handler";
import { useStore } from "@/core/utils/hooks";

import { Sidebar } from "./Sidebar";

export function AppShell({ onSignOut }: { readonly onSignOut: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    state: appState,
    actions: appActions,
    hooks,
  } = useStore(AppStore.instance);
  const { isConnected: notificationsConnected, $notifications } =
    hooks.useAppNotifications();
  const isAuthRoute = useMemo(
    () =>
      location.pathname === APP_ROUTES.auth.signIn.path ||
      location.pathname.startsWith("/auth"),
    [location.pathname],
  );
  const closeDialog = useCallback(() => {
    const onClose = appState?.dialog?.onClose;
    appActions.dialog("close");
    onClose?.();
  }, [appActions, appState?.dialog?.onClose]);

  const closeRightDrawer = useCallback(() => {
    const onClose = appState?.rightDrawer?.onClose;
    appActions.rightDrawer("close");
    onClose?.();
  }, [appActions, appState?.rightDrawer?.onClose]);

  const closeLeftDrawer = useCallback(() => {
    const onClose = appState?.leftDrawer?.onClose;
    appActions.leftDrawer("close");
    onClose?.();
  }, [appActions, appState?.leftDrawer?.onClose]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") {
        return;
      }
      if (appState?.dialog?.shown) {
        closeDialog();
        return;
      }
      if (appState?.rightDrawer?.shown) {
        closeRightDrawer();
        return;
      }
      if (appState?.leftDrawer?.shown) {
        closeLeftDrawer();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    appState?.dialog?.shown,
    appState?.leftDrawer?.shown,
    appState?.rightDrawer?.shown,
    closeDialog,
    closeLeftDrawer,
    closeRightDrawer,
  ]);
  useEffect(() => {
    if (!$notifications || !notificationsConnected) return;
    const subs = $notifications.subscribe({
      next(value) {
        if (value.length <= 0) return;
        const notification = value[0];
        if (!notification) return;
        ErrorHandler.handle(notification.code, () =>
          navigate(APP_ROUTES.auth.signIn.path, { replace: true }),
        );
      },
    });
    return () => {
      if (subs.closed) return;
      subs.unsubscribe();
    };
    // No need to re run  on navigate change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [$notifications, notificationsConnected]);

  return isAuthRoute ? (
    <>
      <main className="min-h-screen bg-app-bg">
        <Outlet />
      </main>
      <AppOverlayHost
        dialog={appState?.dialog}
        isLoading={appState?.isLoading}
        leftDrawer={appState?.leftDrawer}
        onCloseDialog={closeDialog}
        onCloseLeftDrawer={closeLeftDrawer}
        onCloseRightDrawer={closeRightDrawer}
        rightDrawer={appState?.rightDrawer}
      />
    </>
  ) : (
    <>
      <div className="grid h-screen grid-cols-[260px_minmax(0,1fr)] bg-app-bg">
        <Sidebar onSignOut={onSignOut} />
        <main className="overflow-y-auto bg-app-bg">
          <div className="max-w-[1440px] px-[34px] py-7 pb-[60px]">
            <div className="animate-fade-in">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
      <AppOverlayHost
        dialog={appState?.dialog}
        isLoading={appState?.isLoading}
        leftDrawer={appState?.leftDrawer}
        onCloseDialog={closeDialog}
        onCloseLeftDrawer={closeLeftDrawer}
        onCloseRightDrawer={closeRightDrawer}
        rightDrawer={appState?.rightDrawer}
      />
    </>
  );
}

function AppOverlayHost({
  dialog,
  isLoading,
  leftDrawer,
  onCloseDialog,
  onCloseLeftDrawer,
  onCloseRightDrawer,
  rightDrawer,
}: {
  readonly dialog?: AppOverlayPanelStateType;
  readonly isLoading?: boolean;
  readonly leftDrawer?: AppOverlayPanelStateType;
  readonly onCloseDialog: () => void;
  readonly onCloseLeftDrawer: () => void;
  readonly onCloseRightDrawer: () => void;
  readonly rightDrawer?: AppOverlayPanelStateType;
}) {
  return (
    <>
      <DrawerOverlay
        onClose={onCloseLeftDrawer}
        panel={leftDrawer}
        side="left"
      />
      <DrawerOverlay
        onClose={onCloseRightDrawer}
        panel={rightDrawer}
        side="right"
      />
      <DialogOverlay onClose={onCloseDialog} panel={dialog} />
      <LoadingOverlay isLoading={isLoading} />
    </>
  );
}

function DialogOverlay({
  onClose,
  panel,
}: {
  readonly onClose: () => void;
  readonly panel?: AppOverlayPanelStateType;
}) {
  if (!panel?.shown) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-120 flex items-center justify-center bg-black/50 px-5 py-12"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) {
          onClose();
        }
      }}
      role="presentation"
    >
      {panel.children}
    </div>
  );
}

function DrawerOverlay({
  onClose,
  panel,
  side,
}: {
  readonly onClose: () => void;
  readonly panel?: AppOverlayPanelStateType;
  readonly side: "left" | "right";
}) {
  if (!panel?.shown) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 z-110 flex bg-black/40 ${
        side === "left" ? "justify-start" : "justify-end"
      }`}
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) {
          onClose();
        }
      }}
      role="presentation"
    >
      <aside
        className="h-full max-w-[calc(100vw-2rem)] bg-surface shadow-dialog"
        style={{ width: panel.size ?? 540 }}
      >
        {panel.children}
      </aside>
    </div>
  );
}

function LoadingOverlay({ isLoading }: { readonly isLoading?: boolean }) {
  if (!isLoading) {
    return null;
  }

  return (
    <div
      aria-live="polite"
      className="fixed inset-0 z-130 flex items-center justify-center bg-black/45 px-5 py-12"
      role="status"
    >
      <section className="flex min-w-56 flex-col items-center rounded-dialog bg-surface px-8 py-7 text-center shadow-dialog animate-fade-in">
        <img
          alt=""
          aria-hidden="true"
          className="h-28 w-28 animate-pulse"
          src="/favicon.svg"
        />
        <span className="mt-4 text-xs font-semibold text-ink-muted">
          We are making things ready for you.
        </span>
        <span className="text-xs font-semibold text-ink-muted">
          This should only take a moment.
        </span>
      </section>
    </div>
  );
}
